/**
 * act-as-cdn.ts — React Query hooks for CDN domain surfaces via act-as.
 *
 * Covers the org-wide CDN domain list (GET /cdn/domains) and per-domain
 * detail + upstream config (GET /cdn/domains/:id + /upstream). All calls
 * use actAsClient so X-Act-As-Org is injected automatically from the
 * active session.
 *
 * Only usable while getActAsSession() returns a non-null value. The hooks
 * are `enabled`-gated but callers should also guard the render path.
 */

import { useQuery } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface ActAsCDNPagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/** One CDN binding (vendor slot) attached to a domain. */
export interface ActAsCDNBinding {
  binding_id: string;
  provider: string;
  provider_id?: string;
  zone_id?: string;
  uid?: string;
  status?: string;
  enabled?: boolean;
  cname?: string;
}

/** Full CDN domain record — mirrors Partner's CDNDomain. */
export interface ActAsCDNDomainDetail {
  id: string;
  domain: string;
  /** Edge CNAME to point DNS at. */
  cname_target?: string;
  /** Alias kept for compat (same value as cname_target). */
  cname?: string;
  origin?: string;
  status?: string;
  enabled?: boolean;
  tags?: string[];
  bindings?: ActAsCDNBinding[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Upstream/origin config for a CDN domain (from /upstream aspect). */
export interface ActAsCDNUpstreamConfig {
  urls?: string[];
  host_header?: string;
  mode?: 'http' | 's3';
  [key: string]: unknown;
}

/** Page of CDN domains returned by the list endpoint. */
export interface ActAsCDNDomainsPage {
  results: ActAsCDNDomainDetail[];
  pagination: ActAsCDNPagination;
}

/** Query params for the CDN domain list. */
export interface ActAsCDNDomainsParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  enabled?: boolean;
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const actAsCDNKeys = {
  all: ['act-as', 'cdn'] as const,
  domains: (params: ActAsCDNDomainsParams) => ['act-as', 'cdn', 'domains', params] as const,
  domain: (domainId: string) => ['act-as', 'cdn', 'domain', domainId] as const,
  upstream: (domainId: string) => ['act-as', 'cdn', 'domain', domainId, 'upstream'] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * GET /api/v3/cdn/domains — org-wide CDN domain list, paginated.
 * Uses the org context from the active act-as session (X-Act-As-Org).
 * Returns the current page's domains + pagination metadata.
 */
export function useActAsCDNDomains(params: ActAsCDNDomainsParams = {}, enabled = true) {
  const { page = 1, per_page = 50, search, sort = 'domain', order = 'asc' } = params;
  return useQuery({
    queryKey: actAsCDNKeys.domains(params),
    queryFn: async (): Promise<ActAsCDNDomainsPage> => {
      const qp = new URLSearchParams();
      qp.set('page', String(page));
      qp.set('per_page', String(per_page));
      qp.set('sort', sort);
      qp.set('order', order);
      if (search) qp.set('search', search);

      const res = await actAsClient.get<unknown>(
        `/cdn/domains?${qp.toString()}`,
      );
      // BE may return { results, pagination } or { data, pagination }.
      const body = res.data as Record<string, unknown>;
      const items: ActAsCDNDomainDetail[] = (
        Array.isArray(body.results)
          ? (body.results as ActAsCDNDomainDetail[])
          : Array.isArray(body.data)
            ? (body.data as ActAsCDNDomainDetail[])
            : []
      );
      const pagination: ActAsCDNPagination = (body.pagination as ActAsCDNPagination) ?? {
        total: items.length,
        page,
        per_page,
        total_pages: 1,
      };
      return { results: items, pagination };
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * GET /api/v3/cdn/domains/:id — single CDN domain detail.
 * Used by the detail page to populate the Overview tab and the page header.
 */
export function useActAsCDNDomain(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNKeys.domain(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ActAsCDNDomainDetail> => {
      const res = await actAsClient.get<unknown>(
        `/cdn/domains/${domainId}`,
      );
      // Normalise: unwrap { data: { ... } } envelope if present.
      const body = res.data as Record<string, unknown>;
      return (body.data as ActAsCDNDomainDetail) ?? (body as unknown as ActAsCDNDomainDetail);
    },
  });
}

/**
 * GET /api/v3/cdn/domains/:id/upstream — origin/upstream config for this domain.
 * The BE fan-out response contains a `core` key (vEdge — source of truth) and
 * a `vendors` map. We surface the core config for the Overview tab.
 */
export function useActAsCDNUpstream(domainId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: actAsCDNKeys.upstream(domainId ?? ''),
    enabled: Boolean(domainId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<ActAsCDNUpstreamConfig | null> => {
      try {
        const res = await actAsClient.get<unknown>(`/cdn/domains/${domainId}/upstream`);
        // Fan-out envelope: { data: { core: {...}, vendors: {...} } } or flat config.
        const body = res.data as Record<string, unknown>;
        const inner = (body.data ?? body) as Record<string, unknown>;
        // If the BE returns a fan-out structure, pick the `core` config.
        if (inner.core && typeof inner.core === 'object') {
          return inner.core as ActAsCDNUpstreamConfig;
        }
        // Flat config fallback (some providers return config directly).
        if (inner.urls || inner.host_header) {
          return inner as ActAsCDNUpstreamConfig;
        }
        return null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        // 400/404 = domain has no upstream config yet — not an error for the UI.
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}
